import { ceil, cloneDeep, floor, round, toInteger } from 'lodash';
import { getImageTransformInfo } from '@/layers/image-layer/utils/layer';
import { computerImageLayerRatio } from '@/utils/layer-kit';
import {
    flipPoint,
    getLineIntegerPoints,
    pointScale,
    pointsOutOfRangeRectangle,
    rotatePoint,
    translatePointMove,
    translateYReverse,
} from '@/utils/math-kit';
import { IImageLayerProps, ILayerProps, IPoint, MIN_IMG_SIZE } from '@/version-types';

const csc = (rotate: number, distance: number) => {
    const degree = (rotate * Math.PI) / 180;

    if (Math.cos(degree) === 0) {
        return distance;
    }
    return distance / Math.cos(degree);
};

const sec = (rotate: number, distance: number) => {
    const degree = (rotate * Math.PI) / 180;

    if (Math.sin(degree) === 0) {
        return distance;
    }
    return distance / Math.sin(degree);
};

// 获取正方形四个角的位置
export const getCornerPositions = (width: number, height: number) => {
    return {
        leftTop: {
            x: -width / 2,
            y: height / 2,
        },
        rightTop: {
            x: width / 2,
            y: height / 2,
        },
        leftBottom: {
            x: -width / 2,
            y: -height / 2,
        },
        rightBottom: {
            x: width / 2,
            y: -height / 2,
        },
    };
};

// 将图层的坐标转化为图片的坐标
export const transformLayerPositionToImagePoint = (point: IPoint, layer: ILayerProps) => {
    const {
        imageTransform: { flipH, flipV, rotate },
    } = layer.data as IImageLayerProps;
    const { offsetX, offsetY } = getImageTransformInfo(layer);

    const rotateP = rotatePoint(point, rotate);
    const flipP = flipPoint(rotateP, flipH, flipV);

    // 求图层中心点在图片坐标系下的位置   offsetX负号是取反
    const offset = flipPoint(rotatePoint({ x: -offsetX, y: offsetY }, rotate), flipH, flipV);
    const moveP = translatePointMove(flipP, offset.x, offset.y);

    return moveP;
};

// export const transformImagePositionToLayerPoint = (point: IPoint, layer: ILayerProps) => {
//     const {
//         imageTransform: { flipH, flipV, rotate },
//     } = layer.data as IImageLayerProps;
//     const { offsetX, offsetY } = getImageTransformInfo(layer);

//     const rotateP = rotatePoint(point, -rotate);
//     const flipP = flipPoint(rotateP, flipH, flipV);

//     // 求图层中心点在图片坐标系下的位置
//     const offset = flipPoint(rotatePoint({ x: -offsetX, y: -offsetY }, -rotate), flipH, flipV);
//     const moveP = translatePointMove(flipP, -offset.x, -offset.y);

//     return moveP;
// };

// 当超出边界时 调整位置 回到边界(e.displacement会超出 所以要回退一点)
export const adjustPosition = (layer: ILayerProps, moveDistance: IPoint) => {
    const { imageCornerPosition, layerCornerPosition } = getCornerInfos(layer);
    const minX = imageCornerPosition.leftTop.x;
    const maxX = imageCornerPosition.rightBottom.x;
    const minY = imageCornerPosition.rightBottom.y;
    const maxY = imageCornerPosition.leftTop.y;
    const points = [
        layerCornerPosition.leftTop,
        layerCornerPosition.rightTop,
        layerCornerPosition.leftBottom,
        layerCornerPosition.rightBottom,
    ];

    const {
        imageTransform: { flipH, flipV, rotate },
    } = layer.data as IImageLayerProps;

    const offsets = [] as IPoint[];
    for (let i = 0; i < points.length; i++) {
        const point = points[i];
        if (point.x < minX || point.x > maxX || point.y < minY || point.y > maxY) {
            const originOffset = {
                x: point.x < minX ? minX - point.x : point.x > maxX ? maxX - point.x : 0,
                y: point.y < minY ? minY - point.y : point.y > maxY ? maxY - point.y : 0,
            };
            // const adjustOffset = flipPoint(rotatePoint(originOffset, -rotate), flipH, flipV);

            offsets.push({
                x: originOffset.x,
                y: originOffset.y,
            });
        }
    }

    const result = { minX: 0, maxX: 0, minY: 0, maxY: 0 };

    if (offsets.length === 0) {
        return moveDistance;
    }

    offsets.forEach((item) => {
        if (item.x < result.minX) {
            result.minX = item.x;
        }

        if (item.x > result.maxX) {
            result.maxX = item.x;
        }

        if (item.y < result.minY) {
            result.minY = item.y;
        }

        if (item.y > result.maxY) {
            result.maxY = item.y;
        }
    });
    const imgMoveDistance = flipPoint(rotatePoint({ x: moveDistance.x, y: -moveDistance.y }, rotate), flipH, flipV);

    imgMoveDistance.x = imgMoveDistance.x - (result.minX + result.maxX);
    imgMoveDistance.y = imgMoveDistance.y - (result.minY + result.maxY);

    const newMoveDistance = translateYReverse(flipPoint(rotatePoint(imgMoveDistance, -rotate), flipH, flipV));

    return newMoveDistance;
};

export const getCornerInfos = (layer: ILayerProps) => {
    const {
        imageTransform: { scale: imageScale },
    } = layer.data as IImageLayerProps;
    const { originImgWidth, originImgHeight } = getImageTransformInfo(layer);

    let { leftTop, rightTop, leftBottom, rightBottom } = getCornerPositions(layer.width, layer.height);
    leftTop = transformLayerPositionToImagePoint(leftTop, layer);
    rightTop = transformLayerPositionToImagePoint(rightTop, layer);
    leftBottom = transformLayerPositionToImagePoint(leftBottom, layer);
    rightBottom = transformLayerPositionToImagePoint(rightBottom, layer);

    const imageCornerPosition = getCornerPositions(originImgWidth * imageScale, originImgHeight * imageScale);
    return {
        imageCornerPosition,
        layerCornerPosition: {
            leftTop,
            rightTop,
            leftBottom,
            rightBottom,
        },
    };
};

export const adjustLayerSizeChangePosition = (layer: ILayerProps, moveDistance: IPoint, currentOperateType: string) => {
    const {
        imageTransform: { flipH, flipV, rotate },
    } = layer.data as IImageLayerProps;

    const { imageCornerPosition, layerCornerPosition } = getCornerInfos(layer);

    const directionX = flipH ? -1 : 1;
    const directionY = flipV ? -1 : 1;
    const minX = imageCornerPosition.leftTop.x;
    const maxX = imageCornerPosition.rightBottom.x;
    const minY = imageCornerPosition.rightBottom.y;
    const maxY = imageCornerPosition.leftTop.y;
    const points = {
        leftTop: layerCornerPosition.leftTop,
        rightTop: layerCornerPosition.rightTop,
        leftBottom: layerCornerPosition.leftBottom,
        rightBottom: layerCornerPosition.rightBottom,
    };

    const offsets = {} as any;

    Object.keys(points).forEach((key) => {
        const point = points[key];

        if (point.x < minX || point.x > maxX || point.y < minY || point.y > maxY) {
            const originOffset = {
                x: point.x < minX ? minX - point.x : point.x > maxX ? maxX - point.x : 0,
                y: point.y < minY ? minY - point.y : point.y > maxY ? maxY - point.y : 0,
            };

            offsets[key] = {
                x: originOffset.x,
                y: originOffset.y,
            };
        }
    });

    if (Object.keys(points).length === 0) {
        return moveDistance;
    }

    const degree = (rotate * Math.PI) / 180;
    const newMoveDistance = { ...moveDistance };
    if (currentOperateType === 'crop-lt') {
        let offsetX = 0;
        let offsetY = 0;
        if (offsets['leftTop']) {
            const xoffsetX = csc(rotate, offsets['leftTop'].x);
            const yoffsetX = sec(rotate, offsets['leftTop'].y);
            if (Math.abs(xoffsetX) > Math.abs(yoffsetX)) {
                offsetX = xoffsetX;
            } else {
                offsetX = yoffsetX;
            }
        }
        if (offsets['rightTop']) {
            const xOffsetY = sec(rotate, offsets['rightTop'].x);

            if (Math.abs(xOffsetY) > Math.abs(offsetY)) {
                offsetY = xOffsetY;
            }
        }
        if (offsets['leftBottom']) {
            const x = csc(rotate, offsets['leftBottom'].x);

            if (Math.abs(x) > Math.abs(offsetY)) {
                offsetX = x;
            }
        }

        newMoveDistance.x = newMoveDistance.x + offsetX;
        newMoveDistance.y = newMoveDistance.y + offsetY;

        return newMoveDistance;
    }

    return moveDistance;
};

//
export const isImageOutOfRangeLayer = (layer: ILayerProps) => {
    const { imageCornerPosition, layerCornerPosition } = getCornerInfos(layer);

    const isOut = pointsOutOfRangeRectangle(
        [
            layerCornerPosition.leftTop,
            layerCornerPosition.rightTop,
            layerCornerPosition.leftBottom,
            layerCornerPosition.rightBottom,
        ],
        imageCornerPosition,
    );

    return isOut;
};

/**
 * 获取移动相对于中心点的位置
 * @param layer
 * @param moveDistance
 * @param stageScale
 * @returns
 */
export const getMovePositionInImageCenter = (layer: ILayerProps, moveDistance: IPoint, stageScale: number) => {
    const { data, rotate } = layer;
    const { imageTransform } = data as IImageLayerProps;
    const { flipH, flipV, rotate: imageRotate } = imageTransform;

    const delta = flipPoint(
        rotatePoint(translateYReverse(pointScale(moveDistance, 1 / stageScale)), imageRotate + rotate),
        flipH,
        flipV,
    );

    return delta;
};

/**
 * 计算直线 BC 在直线 AB 上的投影点
 * @param {Object} aPoint - 点 A 的坐标，格式为 { x: number, y: number }
 * @param {Object} bPoint - 点 B 的坐标，格式为 { x: number, y: number }
 * @param {Object} cPoint - 点 C 的坐标，格式为 { x: number, y: number }
 * @returns {Object} 投影点的坐标，格式为 { x: number, y: number }
 */
export const calculateProjectionPoint = (aPoint: IPoint, bPoint: IPoint, cPoint: IPoint) => {
    // 计算向量 AB 和 BC
    const AB = { x: bPoint.x - aPoint.x, y: bPoint.y - aPoint.y };
    const BC = { x: cPoint.x - bPoint.x, y: cPoint.y - bPoint.y };

    // 计算向量 AB 的长度
    const abLength = Math.sqrt(AB.x * AB.x + AB.y * AB.y);

    // 计算向量 AB 的单位向量
    const abUnit = { x: AB.x / abLength, y: AB.y / abLength };

    // 计算 BC 在 AB 上的投影长度
    const projectionLength = BC.x * abUnit.x + BC.y * abUnit.y;

    // 计算投影点
    const projectionPoint = {
        x: bPoint.x + abUnit.x * projectionLength,
        y: bPoint.y + abUnit.y * projectionLength,
    };

    return projectionPoint;
};

/**
 * 通过移动的点获取真正的移动位置
 */
export const getMoveDistanceByMoveType = (layer: ILayerProps, scalePosition: string, delta: IPoint) => {
    const { width, height, data, rotate } = layer;
    const { imageTransform } = data as IImageLayerProps;
    const {
        offsetX: offsetXRatio,
        offsetY: offsetYRatio,
        widthRatio,
        heightRatio,
        scale,
        flipH,
        flipV,
        rotate: imageRotate,
    } = imageTransform;
    const imageWidth = ceil(width * widthRatio * scale);
    const imageHeight = ceil(height * heightRatio * scale);

    let movePoint = {
        x: 0,
        y: 0,
    };
    if (scalePosition.includes('lt')) {
        movePoint = {
            x: -imageWidth,
            y: imageHeight,
        };
    } else if (scalePosition.includes('rb')) {
        movePoint = {
            x: imageWidth,
            y: -imageHeight,
        };
    } else if (scalePosition.includes('lb')) {
        movePoint = {
            x: -imageWidth,
            y: -imageHeight,
        };
    } else if (scalePosition.includes('rt')) {
        movePoint = {
            x: imageWidth,
            y: imageHeight,
        };
    }

    const zeroPoint = {
        x: 0,
        y: 0,
    };
    const endPoint = {
        x: movePoint.x + Math.round(delta.x),
        y: movePoint.y + Math.round(delta.y),
    };
    // 投影点
    const moveDistancePoint = calculateProjectionPoint(zeroPoint, movePoint, endPoint);

    const points = getLineIntegerPoints(
        movePoint.x,
        movePoint.y,
        round(moveDistancePoint.x),
        round(moveDistancePoint.y),
    );

    const newSizes = points.map((item) => {
        return {
            width: Math.abs(item.x),
            height: Math.abs(item.y),
        };
    });

    // 返回相对于图层的移动点
    const movePoints = points.map((item) => {
        return translateYReverse(
            rotatePoint(
                {
                    x: -(movePoint.x - item.x) / 2,
                    y: -(movePoint.y - item.y) / 2,
                },

                -imageRotate,
            ),
        );
    });

    return {
        newSizes,
        movePoints,
    };
};

export const getLayerMoveDistanceByMoveResizeType = (layer: ILayerProps, resizePosition: string, delta: IPoint) => {
    const { width, height } = layer;
    let movePoint = {
        x: 0,
        y: 0,
    };

    if (resizePosition.includes('lc') || resizePosition.includes('rc')) {
        movePoint = {
            x: delta.x,
            y: 0,
        };
    } else {
        movePoint = {
            x: 0,
            y: delta.y,
        };
    }

    const points = getLineIntegerPoints(0, 0, movePoint.x, movePoint.y);
    console.log('points===', points);
    console.log('detax===', delta);
    const newSizes = points.map((item) => {
        if (resizePosition.includes('lc')) {
            return {
                width: width - item.x,
                height,
            };
        } else if (resizePosition.includes('rc')) {
            return {
                width: width + item.x,
                height,
            };
        } else if (resizePosition.includes('tc')) {
            return {
                width,
                height: height + item.y,
            };
        } else if (resizePosition.includes('bc')) {
            return {
                width,
                height: height - item.y,
            };
        } else {
            return {
                width,
                height,
            };
        }
    });

    const movePoints = points.map((item) => {
        return translateYReverse({
            x: item.x / 2,
            y: item.y / 2,
        });
    });

    console.log('newSizes====', newSizes);
    return {
        newSizes,
        movePoints,
    };
};

export const getLayerMoveDistanceByMoveType = (
    layer: ILayerProps,
    scalePosition: string,
    delta: IPoint,
    isAutoCutoutLayer: boolean,
) => {
    const { width, height } = layer;

    let movePoint = {
        x: 0,
        y: 0,
    };
    if (scalePosition.includes('lt')) {
        movePoint = {
            x: -width,
            y: height,
        };
    } else if (scalePosition.includes('rb')) {
        movePoint = {
            x: width,
            y: -height,
        };
    } else if (scalePosition.includes('lb')) {
        movePoint = {
            x: -width,
            y: -height,
        };
    } else if (scalePosition.includes('rt')) {
        movePoint = {
            x: width,
            y: height,
        };
    }

    const zeroPoint = {
        x: 0,
        y: 0,
    };
    const endPoint = {
        x: movePoint.x + Math.round(delta.x),
        y: movePoint.y + Math.round(delta.y),
    };
    // 投影点
    const moveDistancePoint = calculateProjectionPoint(zeroPoint, movePoint, endPoint);

    const points = getLineIntegerPoints(
        movePoint.x,
        movePoint.y,
        !isAutoCutoutLayer ? round(moveDistancePoint.x) : endPoint.x,
        !isAutoCutoutLayer ? round(moveDistancePoint.y) : endPoint.y,
    );

    const newSizes = points.map((item) => {
        return {
            width: Math.abs(item.x),
            height: Math.abs(item.y),
        };
    });

    // 返回相对于图层的移动点
    const movePoints = points.map((item) => {
        return translateYReverse({
            x: -(movePoint.x - item.x) / 2,
            y: -(movePoint.y - item.y) / 2,
        });
    });

    // left top得移动距离
    const positionMove = movePoints.map((item) => {
        if (scalePosition.includes('lt')) {
            return {
                x: item.x * 2,
                y: item.y * 2,
            };
        } else if (scalePosition.includes('rb')) {
            return {
                x: 0,
                y: 0,
            };
        } else if (scalePosition.includes('lb')) {
            return {
                x: item.x * 2,
                y: 0,
            };
        } else if (scalePosition.includes('rt')) {
            return {
                x: 0,
                y: item.y * 2,
            };
        }

        return {
            x: 0,
            y: 0,
        };
    });

    return {
        newSizes,
        movePoints,
        positionMove,
    };
};

export const rotateImage = (layer: ILayerProps, rotate: number, originScale?: number) => {
    const newLayer = cloneDeep(layer);
    const { data } = newLayer;
    const { imageTransform } = data as IImageLayerProps;
    imageTransform.rotate = rotate;

    // isZoomOut 是否缩放
    const isOutAndScaleLayer = (outLayer: ILayerProps, isZoomOut: boolean = false) => {
        const isOut = isImageOutOfRangeLayer(outLayer);
        // 超出了就加scale
        // 直到不超出为止
        // 如果是顺时针旋转超出了

        // 在初始角度和上一个角度之间就判断为往回走 不然就是往前走
        const { width, height, data } = outLayer;
        const { imageTransform } = data as IImageLayerProps;
        const { offsetX, offsetY, widthRatio, heightRatio, scale: imageScale } = imageTransform;
        const imageWidth = width * widthRatio * imageScale;
        const imageHeight = height * heightRatio * imageScale;
        const imageOffsetX = offsetX * widthRatio * width * imageScale;
        const imageOffsetY = offsetY * heightRatio * height * imageScale;
        if (isOut && !isZoomOut) {
            const newImageWidth =
                imageWidth < imageHeight ? imageWidth + 1 : (imageHeight + 1) / (imageHeight / imageWidth);
            const newImageHeight =
                imageHeight < imageWidth ? imageHeight + 1 : (imageWidth + 1) / (imageWidth / imageHeight);
            const newImageScale = newImageWidth / (width * widthRatio);
            const newOffsetX = imageOffsetX / newImageWidth;
            const newOffsetY = imageOffsetY / newImageHeight;
            imageTransform.scale = newImageScale;
            imageTransform.offsetX = newOffsetX;
            imageTransform.offsetY = newOffsetY;
            isOutAndScaleLayer(outLayer);
        } else if (!isOut && originScale && originScale < imageScale) {
            const oldLayer = cloneDeep(outLayer);
            const newImageWidth =
                imageWidth > imageHeight ? imageWidth - 1 : (imageHeight - 1) / (imageHeight / imageWidth);
            const newImageHeight =
                imageHeight > imageWidth ? imageHeight - 1 : (imageWidth - 1) / (imageWidth / imageHeight);
            const newImageScale = newImageWidth / (width * widthRatio);
            const newOffsetX = imageOffsetX / newImageWidth;
            const newOffsetY = imageOffsetY / newImageHeight;
            imageTransform.scale = newImageScale;
            imageTransform.offsetX = newOffsetX;
            imageTransform.offsetY = newOffsetY;

            if (!isImageOutOfRangeLayer(outLayer)) {
                isOutAndScaleLayer(outLayer, true);
            } else {
                Object.assign(outLayer, oldLayer);
            }
        }
    };
    isOutAndScaleLayer(newLayer);

    return newLayer;
};

export const adjustImageLayerRatio = (layer: ILayerProps, ratio: string) => {
    const { width, height, data } = layer;
    const { imageTransform } = data as IImageLayerProps;
    const { widthRatio, heightRatio, scale: imageScale } = imageTransform;
    const originImageWidth = width * widthRatio * imageScale;
    const originImageHeight = height * heightRatio * imageScale;

    const originMaxWidth = Math.max(width, height);

    let newLayerWidth = width;
    let newLayerHeight = height;
    const newRatio = ratio.split(':');
    const newLayerWidthRatio = parseInt(newRatio[0]);
    const newLayerHeightRatio = parseInt(newRatio[1]);
    if (newLayerWidthRatio > newLayerHeightRatio) {
        newLayerWidth = originMaxWidth;
        newLayerHeight = (newLayerWidth / newLayerWidthRatio) * newLayerHeightRatio;
    } else {
        newLayerHeight = originMaxWidth;
        newLayerWidth = (newLayerHeight / newLayerHeightRatio) * newLayerWidthRatio;
    }

    const {
        widthRatio: newWidthRatio,
        heightRatio: newHeightRatio,
        scale: newScale,
    } = computerImageLayerRatio(newLayerWidth, newLayerHeight, originImageWidth, originImageHeight);

    const newLayer = cloneDeep(layer);
    const { data: newLayerData } = newLayer;
    const { imageTransform: newImageTransform } = newLayerData as IImageLayerProps;

    newImageTransform.widthRatio = newWidthRatio;
    newImageTransform.heightRatio = newHeightRatio;
    newImageTransform.scale = newScale;
    newLayer.width = toInteger(newLayerWidth);
    newLayer.height = toInteger(newLayerHeight);

    // 如果超出了就等比缩放到不超出
    const isOutAndScaleLayer = (outLayer: ILayerProps) => {
        const isOut = isImageOutOfRangeLayer(outLayer);

        if (isOut) {
            const { width, height, data } = outLayer;
            const { imageTransform } = data as IImageLayerProps;
            const { widthRatio, heightRatio, scale: imageScale } = imageTransform;
            const imageWidth = width * widthRatio * imageScale;
            const imageHeight = height * heightRatio * imageScale;

            const newWidth = width < height ? width - 1 : floor((height - 1) / (height / width));
            const newHeight = height < width ? height - 1 : floor((width - 1) / (width / height));
            const {
                widthRatio: newWidthRatio,
                heightRatio: newHeightRatio,
                scale: newScale,
            } = computerImageLayerRatio(newWidth, newHeight, imageWidth, imageHeight);
            imageTransform.widthRatio = newWidthRatio;
            imageTransform.heightRatio = newHeightRatio;
            imageTransform.scale = newScale;
            outLayer.width = floor(newWidth);
            outLayer.height = floor(newHeight);
            isOutAndScaleLayer(outLayer);
        }
    };
    isOutAndScaleLayer(newLayer);
    newLayer.left = layer.left + (layer.width - newLayer.width) / 2;
    newLayer.top = layer.top + (layer.height - newLayer.height) / 2;
    return newLayer;
};

/**
 * 判断图层是否成比例
 * @param width 宽度
 * @param height 高度
 * @returns 是否成比例
 */
export const isLayerSizeRadio = (width: number, height: number) => {
    const ratio = width / height;
    if (width === height) {
        return '1:1';
    } else if (ratio === 16 / 9) {
        return '16:9';
    } else if (ratio === 9 / 16) {
        return '9:16';
    } else if (ratio === 4 / 3) {
        return '4:3';
    } else if (ratio === 3 / 4) {
        return '3:4';
    } else if (ratio === 1 / 1) {
        return '1:1';
    } else if (ratio === 3 / 2) {
        return '3:2';
    } else if (ratio === 2 / 3) {
        return '2:3';
    }

    return false;
};

// 获取图片图层的中心点
export const getCenterPoint = (element: HTMLElement) => {
    const { left, top, width, height } = element.getBoundingClientRect();
    return {
        x: left + width / 2,
        y: top + height / 2,
    };
};

export const getImageLayerImageSize = (layer: ILayerProps) => {
    const { width, height, data } = layer;
    const { imageTransform } = data as IImageLayerProps;
    const { widthRatio, heightRatio, scale: imageScale } = imageTransform;

    const imageWidth = width * widthRatio * imageScale;
    const imageHeight = height * heightRatio * imageScale;

    return {
        imageWidth,
        imageHeight,
    };
};

export const getLayerCenterPositionMoveDistance = (originLayer: ILayerProps, resultLayer: ILayerProps) => {
    const { left: originLeft, top: originTop, width: originWidth, height: originHeight } = originLayer;
    const { left: resultLeft, top: resultTop, width: resultWidth, height: resultHeight } = resultLayer;

    const originCenterPoint = {
        x: originLeft + originWidth / 2,
        y: originTop + originHeight / 2,
    };
    const resultCenterPoint = {
        x: resultLeft + resultWidth / 2,
        y: resultTop + resultHeight / 2,
    };

    const moveDistance = {
        x: resultCenterPoint.x - originCenterPoint.x,
        y: resultCenterPoint.y - originCenterPoint.y,
    };

    return moveDistance;
};
export const changeLayerStyle = (originLayer: ILayerProps, resultLayer: ILayerProps) => {};
